import React, { useMemo } from 'react';
import { useTranslation } from '@lib/useTypedTranslation';
import { StackedColumnCategoryChart, StackedColumnsCategoryChartProps } from '../../../../components/chart/stackedColumnsCategoryChart';
import { ISmartBatteryHealthIntervalStats } from '../../../../services/shared/battery/batteryCentricService';
import Card from '@components/card/card';
import '../style.css';
import { createChartTooltipAdapter, getTitle } from './lib';

interface IProps {
  data: ISmartBatteryHealthIntervalStats,
  isLoading?: boolean
}

interface BatteryHealthChartData {
  bucket: string,
  count: number
}

const series = [
  {
    visible: true,
    dataKey: 'count' as keyof BatteryHealthChartData,
    description: 'Batteries Health Count',
    colour: '#5b9ad2'
  },
];

export function BatteryHealthChartContainer(props: IProps) {
  const ns = 'batteryEssentialsChart';
  const { t } = useTranslation(ns);

  const chartData: BatteryHealthChartData[] = useMemo(() => {
    if (!props.data) return [];

    const data = [
      {
        bucket: '0-40',
        count: props.data.ZeroToForty,
      },
      {
        bucket: '40-50',
        count: props.data.FortyToFifty,
      },
      {
        bucket: '50-60',
        count: props.data.FiftyToSixty,
      },
      {
        bucket: '60-70',
        count: props.data.SixtyToSeventy,
      },
      {
        bucket: '70-80',
        count: props.data.SeventyToEighty,
      },
      {
        bucket: '80-90',
        count: props.data.EightyToNinety,
      },
      {
        bucket: '90-100',
        count: props.data.NinetyToHundred,
      }
    ];
    return data;
  }, [props.data]);

  const chartProps: StackedColumnsCategoryChartProps<BatteryHealthChartData> = {
    data: !props.isLoading ? chartData : null,
    series,
    title: getTitle(t, 'BATTERY_HEALTH_CHART_TITLE'),
    cypressId: 'battery-health-chart',
    className: 'battery-health-chart',
    hasLegend: false,
    bottomBorder: false,
    columnWidth: 60,
    yAxisType: 'value',
    useAxisTooltip: true,
    amchartsYAxisNumberFormat: '#.',
    categoryKey: 'bucket',
    drawXAxis: (chart, xAxis) => {
      xAxis.title.text = t('BATTERY_HEALTH_CHART_LABEL');
      xAxis.renderer.labels.template.fontSize = 12;
      xAxis.renderer.labels.template.horizontalCenter = 'middle';
      xAxis.renderer.labels.template.verticalCenter = 'middle';
      xAxis.renderer.minGridDistance = 20;
      return xAxis;
    },
    drawYAxis: (chart, yAxis) => {
      yAxis.title.text = t('NO_OF_BATTERIES');
      yAxis.title.fontSize = 12;
      yAxis.renderer.labels.template.fontSize = 12;
      yAxis.min = 0;
      yAxis.strictMinMax = false;
      yAxis.renderer.minGridDistance = 30;
      yAxis.maxPrecision = 0;
      return yAxis;
    },
    getAxisTooltipAdapter: () => createChartTooltipAdapter(t)
  };

  return (
    <Card noPadding={false} className="battery-health-container">
      <StackedColumnCategoryChart<any>
        {...chartProps}
      />
    </Card>
  );
}
